import classNames from "classnames";
import { useState } from "react";
import { toast } from "react-toastify";
import { CloudGovApiError } from "../../../application/domain/api-error";
import { reportUnexpectedError } from "../../../error-reporter";
import { verifyEmail } from "../../../utils/email-utils";
import { BaseModal } from "../../atoms/base-modal";

type UserEmailModalProps = {
  isActive: boolean;
  onModal: () => void;
  issueEmailToken: (email: string) => void;
  verifyEmailToken: (email: string, token: string) => void;
};

export const UserEmailModal: React.FC<UserEmailModalProps> = ({
  isActive,
  onModal,
  issueEmailToken,
  verifyEmailToken,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenIssued, setIsTokenIssued] = useState<boolean>(false);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");

  const onClose = () => {
    setIsTokenIssued(false);
    setEmail("");
    setToken("");
    onModal();
  };

  const onEmailConfirm = async () => {
    setIsLoading(true);
    try {
      await issueEmailToken(email);
      setIsTokenIssued(true);
      toast.success("メールアドレスが登録されました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("確認コードの送信に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitToken = async () => {
    setIsLoading(true);
    try {
      await verifyEmailToken(email, token);
      toast.success("メールアドレスが登録されました。");
      onClose();
    } catch (e) {
      if (
        CloudGovApiError.isErrorCode(e, "bad_request.invalid_user_email_token")
      ) {
        toast.error(
          "メールアドレスの登録に失敗しました。確認コードが正しいかご確認ください。",
        );
      } else {
        toast.error("コードの検証に失敗しました。");
        reportUnexpectedError(e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseModal
      isActive={isActive}
      title={"メールアドレスを登録する"}
      onClose={onClose}
    >
      <section className="modal-card-body">
        {!isTokenIssued ? (
          <>
            <div className="field">
              <p>
                アカウントに登録するメールアドレスを入力してください。入力されたアドレスに登録用の確認コードが送信されます。
              </p>
            </div>
            <div className="field">
              <div className="label">メールアドレス</div>
              <div className="control">
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="field">
              {email}
              に確認コードが送信されました。以下にコードを入力して「送信する」を押すとメールアドレスの登録が完了します。
            </div>
            <div className="field">
              <div className="label">確認コード</div>
              <div className="control">
                <input
                  className="input"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
              </div>
            </div>
          </>
        )}
      </section>

      <footer className="modal-card-foot">
        <button className="button is-failure is-fullwidth" onClick={onClose}>
          キャンセル
        </button>
        <button
          className={classNames("button is-info is-fullwidth", {
            "is-loading": isLoading,
          })}
          disabled={!isTokenIssued ? !verifyEmail(email) : !token}
          onClick={!isTokenIssued ? onEmailConfirm : onSubmitToken}
        >
          送信する
        </button>
      </footer>
    </BaseModal>
  );
};
